/**
 * Industry-Specific Business Concerns Data
 * ========================================
 * Based on ANZSIC 2006 industry classifications
 * Each industry has its own relevant concerns, metrics, and terminology
 */

// Healthcare & Medical (ANZSIC Division Q)
export const HEALTHCARE_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Dental Impression Materials', expected: 50, actual: 38, variance: -12, value: '$240', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'Composite Resins', expected: 30, actual: 25, variance: -5, value: '$175', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Surgical Gloves (Sterile)', expected: 200, actual: 145, variance: -55, value: '$82', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Local Anaesthetic Cartridges', expected: 100, actual: 112, variance: +12, value: '+$36', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'Appointment vs Billing', desc: '12 consultations completed, 10 invoiced - 2 pending', gap: '2 items', value: '$450', severity: 'high' },
    { id: 'disc_002', type: 'Supply Order', desc: 'Medical supplies delivery: 2 items short on order', gap: '2 items', value: '$180', severity: 'high' },
    { id: 'disc_003', type: 'Medicare Claims', desc: '5 claims pending reconciliation - 14 days overdue', gap: '5 claims', value: '$1,240', severity: 'medium' },
    { id: 'disc_004', type: 'Patient Records', desc: '3 patient files missing updated contact information', gap: '3 records', value: 'Compliance', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'No-Show Spike', desc: 'Patient no-shows up 23% this week - review reminder process', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Overtime Hours', desc: 'Clinical staff overtime 20% above roster - 2 consecutive weeks', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Supply Costs', desc: 'Consumables spend 15% above monthly budget', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Treatment Time', desc: 'Average consultation duration up 18% - may need scheduling review', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Clinical Compliance', issue: 'Sterilisation log incomplete for 2 days this week', action: 'Review protocol', severity: 'high' },
    { id: 'risk_002', area: 'AHPRA Registration', issue: '1 practitioner registration renewal due in 21 days', action: 'Submit renewal', severity: 'medium' },
    { id: 'risk_003', area: 'Equipment Maintenance', issue: 'X-ray unit calibration due in 10 days', action: 'Schedule service', severity: 'medium' },
    { id: 'risk_004', area: 'Insurance', issue: 'Professional indemnity renewal due in 30 days', action: 'Review coverage', severity: 'low' },
  ]
};

// Hospitality & Food Services (ANZSIC Division H)
export const HOSPITALITY_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Premium Scotch Fillet', expected: 24, actual: 18, variance: -6, value: '$420', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'House Wine (Shiraz)', expected: 36, actual: 32, variance: -4, value: '$280', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Extra Virgin Olive Oil', expected: 8, actual: 5, variance: -3, value: '$95', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Fresh Seafood Selection', expected: 15, actual: 19, variance: +4, value: '+$180', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'POS vs Inventory', desc: 'Friday dinner: 47 steaks sold, 42 in inventory - 5 unaccounted', gap: '5 units', value: '$175', severity: 'high' },
    { id: 'disc_002', type: 'Delivery Shortage', desc: 'Produce delivery: 3 cases short on seafood order', gap: '3 cases', value: '$450', severity: 'high' },
    { id: 'disc_003', type: 'Wastage Variance', desc: 'Kitchen wastage 12% above normal - review prep process', gap: '+12%', value: '$340/wk', severity: 'medium' },
    { id: 'disc_004', type: 'Till Reconciliation', desc: 'Tuesday PM shift: Till $85 over recorded sales', gap: '+$85', value: '$85', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Beverage COGS', desc: 'Beverage costs up 18% with flat revenue - check pricing/waste', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Labour Hours', desc: 'Overtime hours 35% above roster - 3 consecutive weeks', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Supplier Pricing', desc: 'Produce invoice 8% above contracted rate', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Table Turnover', desc: 'Average table time up 20 mins Saturday night - review service flow', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Food Safety', issue: 'Cool room temp logged at 6°C twice this week (target: 4°C)', action: 'Schedule maintenance', severity: 'high' },
    { id: 'risk_002', area: 'Fire Safety', issue: 'Fire extinguisher inspection overdue by 12 days', action: 'Book inspection', severity: 'medium' },
    { id: 'risk_003', area: 'Staff Compliance', issue: '2 team members RSA expiring within 14 days', action: 'Arrange renewal', severity: 'medium' },
    { id: 'risk_004', area: 'Insurance', issue: 'Public liability renewal due in 21 days', action: 'Review coverage', severity: 'low' },
  ]
};

// Retail Trade (ANZSIC Division G)
export const RETAIL_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Best-Selling Product Line', expected: 48, actual: 31, variance: -17, value: '$1,275', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'Seasonal Stock (Current)', expected: 100, actual: 89, variance: -11, value: '$550', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Premium Gift Items', expected: 24, actual: 19, variance: -5, value: '$375', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Accessories Range', expected: 60, actual: 68, variance: +8, value: '+$160', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'POS vs Stock', desc: 'Saturday sales: 23 items sold, 20 recorded in system', gap: '3 items', value: '$285', severity: 'high' },
    { id: 'disc_002', type: 'Delivery Variance', desc: 'Supplier shipment: 5 units short on order', gap: '5 units', value: '$375', severity: 'high' },
    { id: 'disc_003', type: 'Shrinkage Alert', desc: 'Monthly shrinkage 0.5% above target - review security', gap: '+0.5%', value: '$420/mo', severity: 'medium' },
    { id: 'disc_004', type: 'Returns Processing', desc: '8 returns pending system credit allocation', gap: '8 items', value: '$640', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Sales Drop', desc: 'Wednesday sales 25% below average - check foot traffic', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Labour vs Sales', desc: 'Labour hours up 15% while sales flat - review rostering', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Margin Pressure', desc: 'Gross margin down 3% on promotional items', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Conversion Rate', desc: 'In-store conversion down to 18% (target: 25%)', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Security', issue: 'CCTV camera 3 offline for 2 days - coverage gap', action: 'Schedule repair', severity: 'high' },
    { id: 'risk_002', area: 'Compliance', issue: 'Consumer guarantee signage needs updating', action: 'Order new signs', severity: 'medium' },
    { id: 'risk_003', area: 'Staff Training', issue: 'New POS system training incomplete for 3 staff', action: 'Schedule training', severity: 'medium' },
    { id: 'risk_004', area: 'Insurance', issue: 'Stock insurance review due in 30 days', action: 'Review coverage', severity: 'low' },
  ]
};

// Professional Services (ANZSIC Division M)
export const PROFESSIONAL_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Billable Hours (Monthly)', expected: 160, actual: 142, variance: -18, value: '$3,600', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'WIP (Work in Progress)', expected: 45000, actual: 52000, variance: +7000, value: '+$7,000', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Outstanding Proposals', expected: 8, actual: 12, variance: +4, value: '$48,000', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Active Engagements', expected: 25, actual: 27, variance: +2, value: '+$12,000', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'Time Recording', desc: '14 hours unbilled in draft - senior staff timesheets', gap: '14 hours', value: '$2,800', severity: 'high' },
    { id: 'disc_002', type: 'Invoice Status', desc: '3 invoices sent but not recorded in system', gap: '3 invoices', value: '$8,500', severity: 'high' },
    { id: 'disc_003', type: 'WIP Ageing', desc: 'WIP over 90 days: $18,000 needs billing review', gap: '$18,000', value: 'Cash flow', severity: 'medium' },
    { id: 'disc_004', type: 'Expense Claims', desc: '5 client expense claims pending approval', gap: '5 claims', value: '$1,850', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Utilisation Drop', desc: 'Team utilisation down to 68% (target: 80%)', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Write-offs', desc: 'Write-offs 25% above budget this quarter', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Client Concentration', desc: 'Top 3 clients now 45% of revenue - diversify', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Scope Creep', desc: '4 projects showing hours significantly over estimate', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Professional Indemnity', issue: 'PI insurance renewal due in 14 days', action: 'Review and renew', severity: 'high' },
    { id: 'risk_002', area: 'CPD Compliance', issue: '2 staff CPD hours below requirement', action: 'Schedule courses', severity: 'medium' },
    { id: 'risk_003', area: 'Client Contracts', issue: '3 engagement letters pending signature', action: 'Follow up', severity: 'medium' },
    { id: 'risk_004', area: 'Data Security', issue: 'Annual security audit due next month', action: 'Schedule audit', severity: 'low' },
  ]
};

// Construction (ANZSIC Division E)
export const CONSTRUCTION_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Project Materials Budget', expected: 85000, actual: 92000, variance: +7000, value: '$7,000 over', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'Equipment Hire (Monthly)', expected: 12000, actual: 14500, variance: +2500, value: '$2,500 over', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Safety Equipment Stock', expected: 100, actual: 78, variance: -22, value: '$1,100', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Tool Inventory', expected: 45, actual: 48, variance: +3, value: '+$450', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'Progress Billing', desc: 'Project A: 65% complete, only 50% invoiced', gap: '15%', value: '$45,000', severity: 'high' },
    { id: 'disc_002', type: 'Material Delivery', desc: 'Concrete order: 3 loads short on delivery', gap: '3 loads', value: '$2,400', severity: 'high' },
    { id: 'disc_003', type: 'Labour Hours', desc: 'Subcontractor hours 12% over quoted - verify scope', gap: '+12%', value: '$8,500', severity: 'medium' },
    { id: 'disc_004', type: 'Equipment Returns', desc: '2 hired items returned late - penalty charges', gap: '2 items', value: '$380', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Cost Overrun', desc: 'Project B materials 20% over budget - review procurement', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Schedule Delay', desc: '2 projects behind schedule due to weather - replan', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Rework Costs', desc: 'Defect rectification costs up 35% this month', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Fuel Usage', desc: 'Fleet fuel consumption 15% above estimates', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Safety Compliance', issue: 'SWMS not updated for 2 active sites', action: 'Update immediately', severity: 'high' },
    { id: 'risk_002', area: 'Licensing', issue: 'Builder licence renewal due in 28 days', action: 'Submit renewal', severity: 'medium' },
    { id: 'risk_003', area: 'Workers Comp', issue: 'Premium review due - claims may affect rate', action: 'Review claims', severity: 'medium' },
    { id: 'risk_004', area: 'Contract Variations', issue: '4 variation claims pending client approval', action: 'Follow up', severity: 'low' },
  ]
};

// Wholesale Trade (ANZSIC Division F)
export const WHOLESALE_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Fast-Moving Lines (Top 10)', expected: 500, actual: 412, variance: -88, value: '$4,400', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'Warehouse Capacity (%)', expected: 75, actual: 89, variance: +14, value: 'Overflow risk', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Slow-Moving Stock (>90 days)', expected: 50000, actual: 68000, variance: +18000, value: '-$18,000 tied', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Back Orders', expected: 12, actual: 8, variance: -4, value: 'Good', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'Inventory Count', desc: 'Monthly count: 23 SKUs with variance >5%', gap: '23 SKUs', value: '$12,500', severity: 'high' },
    { id: 'disc_002', type: 'Order Fulfilment', desc: '8 orders shipped incomplete - check picking process', gap: '8 orders', value: 'Reputation', severity: 'high' },
    { id: 'disc_003', type: 'Supplier Invoice', desc: '5 supplier invoices don\'t match PO quantities', gap: '5 invoices', value: '$3,200', severity: 'medium' },
    { id: 'disc_004', type: 'Returns Processing', desc: '12 customer returns pending credit/restock', gap: '12 items', value: '$2,800', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Margin Erosion', desc: 'Average margin down 2.5% - check competitor pricing', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Order Frequency', desc: 'Top customer orders down 30% - contact immediately', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Shipping Costs', desc: 'Freight costs up 18% with flat volume', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Lead Times', desc: 'Supplier lead times extended - review safety stock', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Credit Risk', issue: '3 accounts 60+ days overdue totalling $45,000', action: 'Contact/hold orders', severity: 'high' },
    { id: 'risk_002', area: 'Supply Chain', issue: 'Key supplier has delivery issues - source backup', action: 'Find alternatives', severity: 'medium' },
    { id: 'risk_003', area: 'Insurance', issue: 'Stock insurance valuation needs update', action: 'Request valuation', severity: 'medium' },
    { id: 'risk_004', area: 'System Backup', issue: 'Monthly inventory backup not completed', action: 'Run backup', severity: 'low' },
  ]
};

// Automotive (ANZSIC - Part of Division G/S)
export const AUTOMOTIVE_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Parts Inventory (Fast-Moving)', expected: 200, actual: 156, variance: -44, value: '$2,200', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'Workshop Bays Utilised (%)', expected: 85, actual: 72, variance: -13, value: 'Lost revenue', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Service Consumables', expected: 500, actual: 420, variance: -80, value: '$320', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Warranty Parts Stock', expected: 25, actual: 28, variance: +3, value: '+$450', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'Labour Hours', desc: 'Quoted vs actual: 15 jobs over estimated hours', gap: '24 hours', value: '$2,400', severity: 'high' },
    { id: 'disc_002', type: 'Parts Margin', desc: 'Parts invoiced at incorrect margin on 8 jobs', gap: '8 jobs', value: '$680', severity: 'high' },
    { id: 'disc_003', type: 'WIP Jobs', desc: '5 completed jobs not yet invoiced - 7+ days', gap: '5 jobs', value: '$4,200', severity: 'medium' },
    { id: 'disc_004', type: 'Warranty Claims', desc: '3 warranty claims pending manufacturer submission', gap: '3 claims', value: '$1,850', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Booking Rate', desc: 'Workshop bookings down 20% - review marketing', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Comeback Rate', desc: 'Repeat repairs up to 8% (target: <3%)', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Parts GP', desc: 'Parts gross profit 5% below target this month', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Technician Efficiency', desc: 'Average efficiency at 78% (target: 90%)', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Equipment Calibration', issue: 'Wheel alignment machine calibration overdue', action: 'Schedule service', severity: 'high' },
    { id: 'risk_002', area: 'Trade Licensing', issue: 'Motor dealer licence renewal due in 21 days', action: 'Submit renewal', severity: 'medium' },
    { id: 'risk_003', area: 'Safety', issue: 'Hoist inspection due next week', action: 'Book inspection', severity: 'medium' },
    { id: 'risk_004', area: 'Training', issue: '2 techs need EV certification update', action: 'Schedule course', severity: 'low' },
  ]
};

// Beauty & Personal Care (ANZSIC - Part of Division S)
export const BEAUTY_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Retail Products (Hair Care)', expected: 80, actual: 62, variance: -18, value: '$540', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'Colour Stock (Professional)', expected: 120, actual: 105, variance: -15, value: '$450', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Salon Consumables', expected: 50, actual: 38, variance: -12, value: '$180', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Styling Products', expected: 40, actual: 45, variance: +5, value: '+$125', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'Service vs Booking', desc: '8 appointments completed, 6 in system - 2 missing', gap: '2 bookings', value: '$320', severity: 'high' },
    { id: 'disc_002', type: 'Retail Sales', desc: 'POS shows 15 sales, inventory shows 12 deducted', gap: '3 items', value: '$135', severity: 'high' },
    { id: 'disc_003', type: 'Colour Usage', desc: 'Colour used exceeds service mix calculations', gap: '+15%', value: '$220/wk', severity: 'medium' },
    { id: 'disc_004', type: 'Gift Vouchers', desc: '4 vouchers redeemed but not marked in system', gap: '4 vouchers', value: '$480', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'No-Show Rate', desc: 'No-shows up to 12% this week (target: <5%)', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Rebooking Rate', desc: 'Client rebooking down to 58% (target: 70%)', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Retail Attachment', desc: 'Retail per service at $12 (target: $25)', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Stylist Productivity', desc: '2 stylists below 70% column utilisation', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Product Expiry', issue: '8 colour products expiring within 30 days', action: 'Use or return', severity: 'high' },
    { id: 'risk_002', area: 'Hygiene Compliance', issue: 'Towel sterilisation log incomplete', action: 'Update records', severity: 'medium' },
    { id: 'risk_003', area: 'Staff Certification', issue: '1 stylist needs colour certification renewal', action: 'Book course', severity: 'medium' },
    { id: 'risk_004', area: 'Insurance', issue: 'Salon contents insurance review due', action: 'Schedule review', severity: 'low' },
  ]
};

// Fitness & Wellness (ANZSIC - Part of Division R)
export const FITNESS_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Active Memberships', expected: 500, actual: 462, variance: -38, value: '$2,280/mo', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'PT Sessions (Prepaid)', expected: 200, actual: 245, variance: +45, value: '$4,500 liability', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Retail Stock (Supplements)', expected: 80, actual: 65, variance: -15, value: '$750', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Equipment Towels', expected: 150, actual: 158, variance: +8, value: '+$80', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'Membership Status', desc: '12 failed payments not followed up - still active', gap: '12 members', value: '$720/mo', severity: 'high' },
    { id: 'disc_002', type: 'Class Attendance', desc: 'Class bookings vs check-ins: 15% no-shows', gap: '15%', value: 'Lost spots', severity: 'high' },
    { id: 'disc_003', type: 'PT Revenue', desc: '8 PT sessions completed but not billed', gap: '8 sessions', value: '$640', severity: 'medium' },
    { id: 'disc_004', type: 'Access Control', desc: '3 expired members still accessing facility', gap: '3 members', value: 'Security', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Cancellation Spike', desc: 'Membership cancellations up 40% this month', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Class Utilisation', desc: 'Morning classes at 45% capacity (target: 70%)', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'PT Conversion', desc: 'PT trial to sign-up conversion at 25% (target: 50%)', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Peak Usage', desc: 'Evening peak 20% over capacity - consider expansion', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Equipment Safety', issue: '2 cardio machines need urgent maintenance', action: 'Tag out & service', severity: 'high' },
    { id: 'risk_002', area: 'First Aid', issue: 'Defibrillator pads expiring in 14 days', action: 'Order replacement', severity: 'medium' },
    { id: 'risk_003', area: 'Staff Certs', issue: '1 trainer first aid cert expiring soon', action: 'Schedule renewal', severity: 'medium' },
    { id: 'risk_004', area: 'Insurance', issue: 'Public liability renewal due in 45 days', action: 'Review coverage', severity: 'low' },
  ]
};

// Default/Generic concerns for industries without specific data
export const DEFAULT_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Key Inventory Item A', expected: 100, actual: 82, variance: -18, value: '$900', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'Key Inventory Item B', expected: 50, actual: 45, variance: -5, value: '$250', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Consumables', expected: 200, actual: 175, variance: -25, value: '$125', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Supplies', expected: 75, actual: 80, variance: +5, value: '+$50', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'System vs Actual', desc: 'Monthly reconciliation shows variance', gap: '5%', value: '$1,500', severity: 'high' },
    { id: 'disc_002', type: 'Invoice Mismatch', desc: 'Supplier invoice doesn\'t match order', gap: '3 items', value: '$450', severity: 'high' },
    { id: 'disc_003', type: 'Process Gap', desc: 'Standard procedure not followed - review needed', gap: 'Process', value: 'Risk', severity: 'medium' },
    { id: 'disc_004', type: 'Record Keeping', desc: 'Documentation incomplete for 5 transactions', gap: '5 records', value: 'Compliance', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Revenue Pattern', desc: 'Revenue 15% below forecast this week', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'Cost Variance', desc: 'Operating costs 10% above budget', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Efficiency', desc: 'Process efficiency dropped below target', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Quality', desc: 'Customer complaints above normal level', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Compliance', issue: 'Regulatory requirement review needed', action: 'Schedule review', severity: 'high' },
    { id: 'risk_002', area: 'Licensing', issue: 'Business licence renewal due soon', action: 'Submit renewal', severity: 'medium' },
    { id: 'risk_003', area: 'Insurance', issue: 'Annual insurance review due', action: 'Contact broker', severity: 'medium' },
    { id: 'risk_004', area: 'Training', issue: 'Staff training records need updating', action: 'Schedule training', severity: 'low' },
  ]
};

// Technology / SaaS (ANZSIC Division J / M)
export const TECHNOLOGY_CONCERNS = {
  stocktake: [
    { id: 'stk_001', item: 'Monthly Recurring Revenue (MRR)', expected: 125000, actual: 118500, variance: -6500, value: '$6,500/mo', severity: 'high', lastChecked: '2 days ago' },
    { id: 'stk_002', item: 'Active Subscriptions', expected: 450, actual: 438, variance: -12, value: '-12 accounts', severity: 'medium', lastChecked: '3 days ago' },
    { id: 'stk_003', item: 'Cloud Infrastructure Costs', expected: 28000, actual: 31200, variance: +3200, value: '$3,200 over', severity: 'medium', lastChecked: '1 day ago' },
    { id: 'stk_004', item: 'Annual Contract Value (Pipeline)', expected: 500000, actual: 545000, variance: +45000, value: '+$45,000', severity: 'low', lastChecked: 'Today' },
  ],
  discrepancies: [
    { id: 'disc_001', type: 'Revenue Recognition', desc: 'Stripe payments vs. accounting system: $8,200 unreconciled', gap: '$8,200', value: 'Cash flow', severity: 'high' },
    { id: 'disc_002', type: 'User Metrics', desc: 'Analytics shows 12,400 MAU, billing shows 11,800 - audit needed', gap: '600 users', value: 'Billing leak', severity: 'high' },
    { id: 'disc_003', type: 'Feature Usage', desc: 'Premium feature adoption at 23% (target: 40%)', gap: '-17%', value: 'Churn risk', severity: 'medium' },
    { id: 'disc_004', type: 'Support Tickets', desc: '15 tickets unassigned for >24 hours', gap: '15 tickets', value: 'SLA risk', severity: 'low' },
  ],
  anomalies: [
    { id: 'ano_001', type: 'Churn Spike', desc: 'Customer churn up 2.3% this month (3.8% vs 1.5% target)', severity: 'high', detected: 'Yesterday' },
    { id: 'ano_002', type: 'API Performance', desc: 'Average response time up 40% - investigate infrastructure', severity: 'medium', detected: '3 days ago' },
    { id: 'ano_003', type: 'Conversion Drop', desc: 'Trial-to-paid conversion down to 8% (target: 15%)', severity: 'medium', detected: 'Today' },
    { id: 'ano_004', type: 'Support Volume', desc: 'Support ticket volume up 35% - product issue?', severity: 'low', detected: 'Yesterday' },
  ],
  riskAreas: [
    { id: 'risk_001', area: 'Data Compliance', issue: 'SOC 2 audit due in 21 days - 3 controls need evidence', action: 'Complete documentation', severity: 'high' },
    { id: 'risk_002', area: 'Security', issue: 'Dependency vulnerabilities detected in production', action: 'Run security patch', severity: 'medium' },
    { id: 'risk_003', area: 'Cash Runway', issue: 'Current burn rate gives 8 months runway', action: 'Review spending', severity: 'medium' },
    { id: 'risk_004', area: 'Key Person Risk', issue: 'Single engineer owns critical payment integration', action: 'Cross-train team', severity: 'low' },
  ]
};

// Function to get industry-specific concerns
export const getIndustryConcerns = (industry) => {
  const industryMap = {
    'healthcare': HEALTHCARE_CONCERNS,
    'medical': HEALTHCARE_CONCERNS,
    'dental': HEALTHCARE_CONCERNS,
    'hospitality': HOSPITALITY_CONCERNS,
    'food_beverage': HOSPITALITY_CONCERNS,
    'restaurant': HOSPITALITY_CONCERNS,
    'cafe': HOSPITALITY_CONCERNS,
    'retail': RETAIL_CONCERNS,
    'ecommerce': RETAIL_CONCERNS,
    'professional': PROFESSIONAL_CONCERNS,
    'professional_services': PROFESSIONAL_CONCERNS,
    'legal': PROFESSIONAL_CONCERNS,
    'accounting': PROFESSIONAL_CONCERNS,
    'construction': CONSTRUCTION_CONCERNS,
    'trades': CONSTRUCTION_CONCERNS,
    'building': CONSTRUCTION_CONCERNS,
    'wholesale': WHOLESALE_CONCERNS,
    'distribution': WHOLESALE_CONCERNS,
    'automotive': AUTOMOTIVE_CONCERNS,
    'mechanic': AUTOMOTIVE_CONCERNS,
    'beauty': BEAUTY_CONCERNS,
    'salon': BEAUTY_CONCERNS,
    'spa': BEAUTY_CONCERNS,
    'fitness': FITNESS_CONCERNS,
    'gym': FITNESS_CONCERNS,
    'wellness': FITNESS_CONCERNS,
    'technology': TECHNOLOGY_CONCERNS,
    'saas': TECHNOLOGY_CONCERNS,
    'software': TECHNOLOGY_CONCERNS,
    'it': TECHNOLOGY_CONCERNS,
    'it_services': TECHNOLOGY_CONCERNS,
    'media_telecom': TECHNOLOGY_CONCERNS,
    'telecommunications': TECHNOLOGY_CONCERNS,
  };
  
  const normalizedIndustry = industry?.toLowerCase().replace(/[^a-z]/g, '_') || 'default';
  return industryMap[normalizedIndustry] || DEFAULT_CONCERNS;
};

// Owner title mapping by industry
export const getOwnerTitle = (industry) => {
  const titles = {
    'healthcare': 'Practice Principal',
    'medical': 'Practice Principal',
    'dental': 'Practice Principal',
    'hospitality': 'Venue Owner',
    'restaurant': 'Restaurant Owner',
    'cafe': 'Café Owner',
    'bar': 'Bar Owner',
    'retail': 'Store Owner',
    'ecommerce': 'Business Owner',
    'professional': 'Managing Director',
    'legal': 'Managing Partner',
    'accounting': 'Practice Principal',
    'construction': 'Director',
    'trades': 'Business Owner',
    'wholesale': 'Managing Director',
    'distribution': 'Managing Director',
    'automotive': 'Dealer Principal',
    'mechanic': 'Workshop Owner',
    'beauty': 'Salon Owner',
    'spa': 'Spa Owner',
    'fitness': 'Gym Owner',
    'gym': 'Gym Owner',
    'education': 'Principal',
    'technology': 'Founder',
    'saas': 'Founder',
  };
  
  const normalizedIndustry = industry?.toLowerCase().replace(/[^a-z]/g, '_') || 'default';
  return titles[normalizedIndustry] || 'Business Owner';
};
